<template>
  <div class="tag__wrapper">
    <a-sticky-banner
      :sticky-position="STICKY_BANNER_POSITION.TOP"
      :banner-settings="BANNER_SETTINGS.TAG_PAGE.STICKY_TO_TOP"
    />

    <tag-info v-bind="tag" />

    <a-paginated-news-list
      ref="paginatedNewsList"
      page-type="allNews"
      :pagination="pagination"
      :pages-count="pagesCount"
      hide-categories-strip
      hide-most-viewed-strip
      :show-telegram-widget="false"
      :all-articles="articles"
      :in-progress="isRequestInProgress"
      :banner-position="BANNER_POSITION"
      :title-html-tag="HTML_TAG.HEADING_H2"
      class="tag__list"
      @get-new-chunk="getNextArticleChunk"
    />
    <a-pagination-links-for-google
      :page="pagination.page"
      :pages-count="pagesCount"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />
    <a-url-pagination
      :item-count="articles.length"
      :pagination.sync="pagination"
      :initial-page="initialPage"
      :pages-count="pagesCount"
      :item-selector="$options.consts.ARTICLE_SLOT_SELECTOR"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import mixins from '@/utils/mixins'
import {
  METHOD_NAME_TO_RUN_ON_LOGOUT,
  METHOD_NAME_TO_RUN_ON_LOGIN
} from '@/utils/mixins/run-method-on-login-logout'
import { PAGE_SCHEME_TYPE } from '@/utils/enums/pageSchemes'
import APaginatedNewsList from 'shared/APaginatedNewsList'
import { ARTICLE_SLOT_CLASS_NAME } from 'shared/APaginatedNewsList/enums'
import TagInfo from '@/components/_sections/tag/TagInfo'
import { throwPageNotFoundError } from '@/plugins/error'
import { BANNER_SETTINGS } from 'enums/banners/banner-settings'
import AStickyBanner from 'shared/AStickyBanner'
import { STICKY_BANNER_POSITION } from 'enums/banners/sticky-banner'
import { HTML_TAG } from '@/utils/helpers/processHtml/enums'
import APaginationLinksForGoogle from 'shared/APaginationLinksForGoogle'
import { applyFallback } from '@/utils/helpers'
import AUrlPagination from 'shared/AUrlPagination/index.vue'
import { getPageNumberFromRoute } from '@/plugins/helper'
import { METHOD_NAME, PENDING_ACTION } from 'enums/pending-actions'
import { generateTagUrl } from '@/utils/helpers/url-generators'
import {
  generatePaginatedCanonicalUrl,
  checkIfNonCanonicalPath
} from '@/utils/helpers/canonical-links'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import { handleNextChunks } from '@/utils/helpers/handle-duplicates'
import { pick } from 'ramda'

const TAG_MODEL = {
  Id: null,
  Name: '',
  CompanyInfo: {
    Id: null,
    Name: '',
    Description: '',
    Slug: '',
    LogoUrl: ''
  },
  TermInfo: {
    Id: null,
    Description: '',
    Slug: ''
  }
}

const DEFAULT_TAKE = 25
const BANNER_POSITION = 4

async function getTagPageData({ ctx, route, store }) {
  const { slug: tagSlug } = route.params
  const initialPage = getPageNumberFromRoute(route)

  const pagination = {
    take: DEFAULT_TAKE,
    page: initialPage
  }

  const { dispatch } = store

  const initialPayload = {
    tagSlug,
    ...pagination
  }

  const requests = [
    dispatch('tag/requestTagBySlug', {
      pathParams: {
        slug: tagSlug
      }
    }),
    dispatch('tag/requestArticleByTagSlug', initialPayload)
  ]

  const [Tag, { Articles, PagesCount }] = await Promise.all(requests)

  if (PagesCount && initialPage > PagesCount) {
    return throwPageNotFoundError(ctx, 'TagPage')
  }

  return {
    tag: applyFallback(Tag, TAG_MODEL),
    articles: Articles,
    initialPage,
    pagesCount: PagesCount,
    pagination
  }
}

function generateDefaultTagContentDataModel() {
  return {
    tag: null,
    articles: []
  }
}

export default {
  name: 'SingleTagPage',
  validate({ params: { slug } }) {
    return !!slug
  },
  scrollToTop: true,
  mixins: [
    mixins.runMethodOnLoginLogout,
    mixins.refreshDataHandler,
    mixins.setPageLoaded,
    mixins.refreshDataListenersFactory({
      refreshHandlerByType: {
        [PENDING_ACTION.SAVE_ARTICLE]: METHOD_NAME.UPDATE_IS_SAVED_VALUE,
        [PENDING_ACTION.FOLLOW_TAG]: METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE
      }
    }),
    mixins.delayedContentRefresh
  ],
  components: {
    AStickyBanner,
    APaginatedNewsList,
    TagInfo,
    APaginationLinksForGoogle,
    AUrlPagination
  },
  head() {
    const { Name, CompanyInfo, TermInfo } = this.tag
    const link = this.$helper.getFullPath(this)
    const description = CompanyInfo.Description || TermInfo.Description || Name

    const webPageSchemeData = {
      relatedLink: link,
      significantLink: link,
      alternativeHeadline: Name,
      headline: Name,
      keywords: Name,
      text: description,
      description,
      mainEntityOfPage: 'Tag page',
      name: Name
    }

    return this.$generateMetaTags({
      titleParam: Name,
      descriptionParam: Name,
      schemes: [
        { type: PAGE_SCHEME_TYPE.ORGANIZATION },
        { type: PAGE_SCHEME_TYPE.WEB_PAGE, data: webPageSchemeData }
      ],
      canonicalUrl: this.canonicalUrl
    })
  },
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route

    try {
      const tagData = await getTagPageData({
        ctx,
        route: ctx.route,
        store: ctx.store
      })

      const canonicalUrlWithoutPagination = generateTagUrl(tagData.tag, ctx)
      const canonicalUrl = generatePaginatedCanonicalUrl({
        canonicalUrlWithoutPagination,
        path: ctx.route.path
      })

      if (checkIfNonCanonicalPath(ctx, { currentPath, canonicalUrl })) {
        redirectHandler({ ctx, to: canonicalUrl })
        return
      }

      return {
        ...tagData,
        canonicalUrl,
        canonicalUrlWithoutPagination
      }
    } catch (err) {
      throwPageNotFoundError(ctx, 'TagPage')
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  consts: {
    ARTICLE_SLOT_SELECTOR: `.${ARTICLE_SLOT_CLASS_NAME}`
  },
  data() {
    const initialPage = getPageNumberFromRoute(this.$route)

    return {
      ...generateDefaultTagContentDataModel(),
      canonicalUrl: this.$route.path,
      canonicalUrlWithoutPagination: this.$route.path,
      BANNER_SETTINGS,
      BANNER_POSITION,
      STICKY_BANNER_POSITION,
      HTML_TAG,
      initialPage,
      pagesCount: 0,
      pagination: {
        take: DEFAULT_TAKE,
        page: initialPage
      },
      shift: 0,
      inProgress: false
    }
  },
  computed: {
    isRequestInProgress() {
      return this.inProgress || this.isPendingContentRefresh
    }
  },
  methods: {
    ...mapActions({
      requestArticleByTagSlug: 'tag/requestArticleByTagSlug'
    }),
    async getArticles(pagination) {
      try {
        this.inProgress = true
        const { Articles, PagesCount } = await this.requestArticleByTagSlug({
          tagSlug: this.$helper.getLastPathParam(this.$route.path),
          ...pagination
        })
        this.pagesCount = PagesCount

        return Articles
      } catch (err) {
        this.$errorHandler(err, this, {
          showErrorPage: true,
          showMessage: false
        })
      }
    },
    getNextArticleChunk() {
      this.pagination.page++
      handleNextChunks({
        shift: this.shift,
        pagination: this.pagination,
        pagesCount: this.pagesCount,
        setInProgressTrueFn: () => {
          this.inProgress = true
        },
        setInProgressFalseFn: () => {
          this.inProgress = false
        },
        setShiftFn: newVal => {
          this.shift = newVal
        },
        requestNextChunkFn: this.getArticles.bind(this),
        addNewArticlesFn: async newArticles => {
          this.articles = [...this.articles, ...newArticles]
          await this.$nextTick()
        },
        getExistingArticleIdsFn: () => this.articles.map(({ id }) => id)
      })
    },
    [METHOD_NAME.UPDATE_IS_SAVED_VALUE](payload) {
      this.articles = this.$_refreshDataHandler_updateIsSavedValue(payload)
    },
    [METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE](payload) {
      this.articles = this.$_refreshDataHandler_updateIsFollowedTagValue(
        payload
      )
      this.tag.IsFollowed = payload.value
    },
    [METHOD_NAME_TO_RUN_ON_LOGOUT]() {
      this.articles = this.$_refreshDataHandler_resetActionIconValues()
      this.tag.IsFollowed = false
    },
    async [METHOD_NAME_TO_RUN_ON_LOGIN]() {
      const {
        Article,
        Tag
      } = await this.$_refreshDataHandler_getSavedIdsOfEntities([
        this.$_refreshDataHandler_savedEntity.ARTICLE,
        this.$_refreshDataHandler_savedEntity.TAG
      ])
      this.articles = this.$_refreshDataHandler_updateArticleSavedEntities({
        articleIds: Article,
        tagIds: Tag
      })
      this.tag.IsFollowed = (Tag || []).includes(this.tag.Id)
    }
  },
  mounted() {
    const dataKeysForDelayedContentRefresh = Object.keys(
      generateDefaultTagContentDataModel()
    )

    this.$_delayedContentRefresh_run({
      getOldDataFn: () => pick(dataKeysForDelayedContentRefresh, this),
      requestNewDataFn: async () => {
        const newData = await getTagPageData({
          ctx: this,
          route: this.$route,
          store: this.$store
        })

        return pick(dataKeysForDelayedContentRefresh, newData)
      },
      dataChangedCallbackFn: newData => {
        Object.assign(this, pick(dataKeysForDelayedContentRefresh, newData))
        this.$refs.paginatedNewsList.rerenderNonHydratedComponents()
      }
    })
  }
}
</script>

<style lang="scss" scoped>
.tag__wrapper {
  margin-top: 65px;

  @include mobile {
    margin-top: 50px;
  }
}
</style>
